import { useState, useEffect } from "preact/hooks";
import { useRoute } from "wouter-preact";
import { ClientInvitation, InvitationData } from "@marginwiz/shared-ui";
import { api_fetch } from "@/api/client";

interface ContractConfig {
  monthly_price: number;
  commission_percent: number;
}

interface ClientInvitationResponse {
  id: string;
  created_at: string;
  client_name: string;
  common_name: string;
  contract_config: ContractConfig;
  logo_s3_url: string;
}

export function InvitePage() {
  const [, params] = useRoute("/invite/:commonName");
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    async function loadInvitation() {
      if (!params?.commonName) {
        setError("Invalid invitation URL");
        setLoading(false);
        return;
      }

      try {
        const response = await api_fetch<ClientInvitationResponse>(
          `/client-invitations/${params.commonName}`
        );

        if (!response.ok) {
          setError("Invitation not found");
          setLoading(false);
          return;
        }

        const data = response.value.data;
        setInvitation({
          client_name: data.client_name,
          logo_s3_url: data.logo_s3_url,
          contract_config: data.contract_config,
        });
      } catch (err) {
        setError("Failed to load invitation");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    loadInvitation();
  }, [params?.commonName]);

  async function handleAcceptACH(routingNumber: string, accountNumber: string) {
    setIsSubmitting(true);
    try {
      // TODO: Implement organization creation and ACH setup
      console.log("ACH setup:", { routingNumber, accountNumber });
      alert("ACH setup functionality coming soon!");
    } catch (err) {
      console.error("Failed to accept invitation:", err);
      alert("Failed to accept invitation");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleAcceptStripe() {
    setIsSubmitting(true);
    try {
      // TODO: Implement organization creation and Stripe redirect
      console.log("Stripe setup");
      alert("Stripe setup functionality coming soon!");
    } catch (err) {
      console.error("Failed to accept invitation:", err);
      alert("Failed to accept invitation");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div class="min-h-screen bg-gray-50 flex items-center justify-center">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p class="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div class="min-h-screen bg-gray-50 flex items-center justify-center">
        <div class="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
          <svg
            class="w-16 h-16 text-red-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h1 class="text-2xl font-bold text-gray-900 mb-2">Invitation Not Found</h1>
          <p class="text-gray-600">{error || "The invitation you're looking for doesn't exist."}</p>
        </div>
      </div>
    );
  }

  return (
    <div class="min-h-screen bg-gray-50">
      <ClientInvitation
        data={invitation}
        onAcceptACH={handleAcceptACH}
        onAcceptStripe={handleAcceptStripe}
        showPaymentOptions={true}
      />
    </div>
  );
}
